import React from 'react';

interface BankStatementSectionProps {
  /** One { bank | safe, totals, entries } block from the banks/safes report API */
  block: any;
}

const fmtDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('en-GB') : '-';

const fmtAmount = (value: any) => Number(value || 0).toLocaleString();

/** One bank's or safe's statement: header row with totals + debit/credit entries table */
const BankStatementSection: React.FC<BankStatementSectionProps> = ({ block }) => {
  const { totals, entries } = block;
  const account = block.bank || block.safe || {};
  const subtitleParts = [
    account.accountNumber ? `Account: ${account.accountNumber}` : '',
    account.accountType || '',
    account.location ? `Location: ${account.location}` : '',
    account.responsiblePerson ? `Responsible: ${account.responsiblePerson}` : '',
    account.currency || '',
  ].filter(Boolean);
  const net = Number(totals?.net || 0);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{account.name}</h3>
          <p className="text-sm text-gray-500">{subtitleParts.join(' · ')}</p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="text-gray-600">Balance: <strong className="text-gray-900">{fmtAmount(account.currentBalance)}</strong></span>
          <span className="text-green-700">Credit: <strong>{fmtAmount(totals?.totalCredit)}</strong></span>
          <span className="text-red-700">Debit: <strong>{fmtAmount(totals?.totalDebit)}</strong></span>
          <span className={net >= 0 ? 'text-green-700' : 'text-red-700'}>Net: <strong>{fmtAmount(net)}</strong></span>
          <span className="text-gray-600">Entries: <strong className="text-gray-900">{totals?.entriesCount ?? entries?.length ?? 0}</strong></span>
        </div>
      </div>

      {(!entries || entries.length === 0) ? (
        <p className="text-sm text-gray-500 px-6 py-4">No entries for this account in the selected period</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Type', 'Credit (In)', 'Debit (Out)', 'Description', 'Cheque', 'Reference', 'Balance After'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((e: any, idx: number) => {
                const isCredit = e.direction === 'credit';
                return (
                  <tr key={e.transactionDocumentId || `${e.date}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(e.date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        e.source === 'adjustment' ? 'bg-yellow-100 text-yellow-800'
                          : isCredit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {e.source === 'adjustment' ? 'Adjustment' : isCredit ? 'In' : 'Out'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700 font-medium">
                      {isCredit ? `${e.currency ? `${e.currency} ` : ''}${fmtAmount(e.amount)}` : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-700 font-medium">
                      {!isCredit ? `${e.currency ? `${e.currency} ` : ''}${fmtAmount(e.amount)}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{e.description || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {e.chequeNumber ? `${e.chequeNumber}${e.chequeDate ? ` (${fmtDate(e.chequeDate)})` : ''}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {e.reference ? `${e.reference.type}: ${e.reference.description || e.reference.documentId}` : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {e.balanceAfter !== null && e.balanceAfter !== undefined
                        ? `${fmtAmount(e.balanceBefore)} → ${fmtAmount(e.balanceAfter)}`
                        : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BankStatementSection;
